/**
 * ログインページ。Firebase Authentication（Google）によるサインイン/サインアウトと
 * 状態表示、認可エラーハンドリングを提供します。
 * - 役割: ユーザーの認証を行い、アプリ内の保護ページへの導線を表示
 * - 備考: 認証状態は `auth` のリスナーで監視し、UIを切り替えます
 */
"use client";

import { useState, useEffect, useMemo, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Bot, Loader2 } from 'lucide-react';
import { auth, login, isPopupRunning } from '@/lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';

// App Router の静的最適化を無効化（毎回クライアント実行）
export const dynamic = 'force-dynamic';

function LoginPageContent() {
  const [isLoading, setIsLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string>('');
  const router = useRouter();
  const searchParams = useSearchParams();
  const defaultNext = useMemo(() => `/meeting/${crypto.randomUUID()}/live`, []);

  // ★修正：自動遷移を抑止（auto=1 または next パラメータがある時のみ遷移）
  useEffect(() => {
    console.log('[login] mounting - setting up auth listener');
    
    const next = searchParams?.get("next") || defaultNext;
    const allowAuto = searchParams?.get("auto") === "1";
    
    // onAuthStateChanged でログイン状態を監視（一元化）
    const unsub = onAuthStateChanged(auth, (user) => {
      console.log('[login] onAuthStateChanged:', user ? `user ${user.uid}` : 'null');
      
      // ★Popup実行中は先走り遷移を抑止
      if (isPopupRunning()) {
        console.log('[login] popup in progress, deferring navigation');
        return;
      }
      
      if (user) {
        // ★既ログインでも、auto=1 または next パラメータがない限り遷移しない
        if (allowAuto || searchParams?.get("next")) {
          console.log('[login] user authenticated, auto navigation allowed, redirecting to', next);
          router.replace(next);
        } else {
          // ★既ログインだが自動遷移は抑止→ボタン表示
          console.log('[login] user authenticated, but auto navigation disabled, staying on login page');
          setIsLoading(false);
        }
      } else {
        // 未ログイン → ログイン画面に留まる
        console.log('[login] no user, staying on login page');
        setIsLoading(false);
      }
    });

    return () => {
      console.log('[login] unmounting - cleanup auth listener');
      unsub();
    };
  }, [router, searchParams, defaultNext]);

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      // ★既にログイン済みなら即座に遷移
      if (auth.currentUser) {
        const next = searchParams?.get("next") || defaultNext;
        console.log('[login] already logged in, redirecting to', next);
        router.replace(next);
        return;
      }
      
      // Popup でサインイン（すべての環境で統一）
      const user = await login();
      console.log('[login] popup success, user:', user.uid);
      
      // ★明示的にログイン操作をしたので、auto=1 なしでも遷移
      const next = searchParams?.get("next") || defaultNext;
      router.replace(next);
    } catch (e: any) {
      console.error('[login] signin error:', e.code, e.message);
      setIsLoading(false);
      // TODO: エラーメッセージを表示
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Card className="w-full max-w-sm bg-card border-border">
        <CardHeader className="text-center">
          <div className="flex justify-center items-center mb-4">
             <Bot className="w-12 h-12 text-primary" />
          </div>
          <CardTitle className="text-2xl font-headline">会議NAVIへようこそ</CardTitle>
        </CardHeader>
        <CardContent>
          <Button 
            className="w-full" 
            onClick={handleGoogleSignIn}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ログイン中...
              </>
            ) : (
              'Googleでログイン'
            )}
          </Button>
          {debugInfo && (
            <div className="mt-4 p-2 bg-muted rounded text-xs text-muted-foreground break-all">
              {debugInfo}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Suspense で囲んでエクスポート（Next.js 15 要件）
export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="w-full max-w-sm bg-card border-border">
          <CardContent className="pt-6">
            <div className="flex justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>
    }>
      <LoginPageContent />
    </Suspense>
  );
}
